import type { ReactNode } from 'react';

import type { SerializedBlock } from '@zoomit/editor';
import { Col, Flex, Spacing } from '@zoomit/primitives';
import { getBlockContainerFloat, getBlockContainerType, isMobile, renderRemoteBlock } from '@zoomit/web-kit';

import { BlockContainer } from './BlockContainer';

type Props = {
  contentData: SerializedBlock[];
  containerVariant?: 'small' | 'medium';
};

const ContentParser = (props: Props) => {
  const { contentData, containerVariant } = props;

  const renderFloatBlocks = (
    firstBlock: SerializedBlock,
    secondBlock: SerializedBlock,
    float: 'right' | 'left',
  ) => (
    <Flex
      flexDirection={float === 'right' ? 'row' : 'row-reverse'}
      gutter={isMobile ? 0 : Spacing.x6}
      alignItems="center"
    >
      <Col size={12} md={6}>
        {renderRemoteBlock?.(firstBlock)}
      </Col>
      <Col size={12} md={6}>
        {renderRemoteBlock?.(secondBlock)}
      </Col>
    </Flex>
  );

  const renderBlocks = (blocks: SerializedBlock[]) => {
    //TODO: below codes become a bit dirty, we need to refactor it.
    const elements: ReactNode[] = [];
    for (let currIndex = 0; currIndex < blocks.length; ) {
      const currBlock = blocks[currIndex];

      if (isMobile) {
        //----------block----------------------------------------------------------------------------
        //in mobile, all blocks are vertical.
        elements.push(
          <BlockContainer
            containerVariant={containerVariant}
            template={(currBlock as any).template}
            key={currBlock.id}
          >
            {renderRemoteBlock?.(currBlock)}
          </BlockContainer>,
        );
        currIndex++;
      } else {
        // some block templates (e.g. related-article, keyword, ...) change next block template.
        //----------block----------------------------------------------------------------------------
        const currBlockTemplate = (currBlock as any).template;
        const currBlockTemplateType = getBlockContainerType(currBlockTemplate);
        const currBlockFloat = getBlockContainerFloat(currBlockTemplate);
        const sameBlocksContainer = [];
        let numOfBlocksInSameContainer = 0;

        if (
          currBlockFloat !== 'normal' &&
          currIndex !== blocks.length - 1 &&
          blocks[currIndex + 1].type === 'typography'
        ) {
          sameBlocksContainer.push(renderFloatBlocks(currBlock, blocks[currIndex + 1], currBlockFloat));
          numOfBlocksInSameContainer += 2;
        } else {
          sameBlocksContainer.push(renderRemoteBlock?.(currBlock));
          numOfBlocksInSameContainer++;
        }

        for (let nextIndex = currIndex + numOfBlocksInSameContainer; nextIndex < blocks.length; nextIndex++) {
          const nextBlock = blocks[nextIndex];
          const nextBlockTemplate = (nextBlock as any).template;
          const nextBlockTemplateType = getBlockContainerType(nextBlockTemplate);
          const nextBlockFloat = getBlockContainerFloat(nextBlockTemplate);

          if (currBlockTemplateType === nextBlockTemplateType) {
            if (
              nextBlockFloat !== 'normal' &&
              nextIndex !== blocks.length - 1 &&
              blocks[nextIndex + 1].type === 'typography'
            ) {
              sameBlocksContainer.push(renderFloatBlocks(nextBlock, blocks[nextIndex + 1], nextBlockFloat));
              numOfBlocksInSameContainer += 2;
              nextIndex++;
            } else {
              sameBlocksContainer.push(renderRemoteBlock?.(nextBlock));
              numOfBlocksInSameContainer++;
            }
          } else break;
        }

        elements.push(
          <BlockContainer containerVariant={containerVariant} template={currBlockTemplate} key={currBlock.id}>
            {sameBlocksContainer}
          </BlockContainer>,
        );
        currIndex += numOfBlocksInSameContainer;
      }
    }

    return elements;
  };

  return (
    <Flex flexDirection="column" gap={1.6}>
      {renderBlocks(contentData)}
    </Flex>
  );
};

export default ContentParser;
